<template>
  <div class="video-player">
    <video ref="videoPlayer" class="video video-js">
      <!--
        <track ref="subtitles" kind="subtitles" v-if="options.subtitles" :src="options.subtitles" />
      -->
    </video>
  </div>
</template>

<script>
import { isNil } from 'lodash';
import 'video.js/dist/video-js.css';
import _videojs from 'video.js';
import isMobile from 'ismobilejs';

const videojs = window.videojs || _videojs;

/* eslint no-underscore-dangle: 0 */
export default {
  name: 'VideoPlayer',
  props: {
    start: {
      type: Number,
      default: 0,
    },
    crossOrigin: {
      type: String,
      default: '',
    },
    playsinline: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object,
      default() {
        return {};
      },
    },
    events: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      player: null,
      globalEvents: [],
      globalOptions: {
        autoplay: true,
        controls: false,
        language: 'en',
        inactivityTimeout: 0,
        preload: 'auto',
        techOrder: ['html5'],
      },
    };
  },

  methods: {
    initialize() {
      const self = this;
      let videoObj = this.$refs.videoPlayer;
      // videojs options
      const videoOptions = Object.assign({}, this.globalOptions, this.options);
      // ios fullscreen
      if (this.playsinline) {
        videoObj.setAttribute('playsinline', this.playsinline);
        videoObj.setAttribute('webkit-playsinline', this.playsinline);
        videoObj.setAttribute('x5-playsinline', this.playsinline);
        videoObj.setAttribute('x5-video-player-type', 'h5');
        videoObj.setAttribute('x5-video-player-fullscreen', false);
      }
      // cross origin
      if (this.crossOrigin !== '') {
        videoObj.crossOrigin = this.crossOrigin;
        videoObj.setAttribute('crossOrigin', this.crossOrigin);
      }
      // avoid error "VIDEOJS: ERROR: Unable to find plugin: __ob__"
      if (videoOptions.plugins) {
        delete videoOptions.plugins.__ob__;
      }
      // emit event
      const emitPlayerState = (event) => {
        if (event) {
          this.$emit(event, this.player);
        }
      };
      // player
      /* eslint-disable */
      this.player = videojs(videoObj, videoOptions, function () {
        // events
        const DEFAULT_EVENTS = [
          'loadeddata',
          'canplay',
          'canplaythrough',
          'play',
          'pause',
          'waiting',
          'playing',
          'ended',
          'error',
        ];
        const events = DEFAULT_EVENTS.concat(self.events).concat(self.globalEvents);

        // watch events
        const onEdEvents = {};
        for (let i = 0; i < events.length; i += 1) {
          if (typeof events[i] === 'string' && onEdEvents[events[i]] === undefined) {
            ((event) => {
              onEdEvents[event] = null;
              this.on(event, () => {
                emitPlayerState(event, true);
              });
            })(events[i]);
          }
        }
        // player readied
        self.$emit('ready', this);
      });

      // Add "remote" text tracks programmatically
      this.player.ready(() => {
        if (videoOptions.textTracks && videoOptions.textTracks.src) {
          this.player.addRemoteTextTrack(
            {
              src: videoOptions.textTracks.src,
              kind: videoOptions.textTracks.kind || 'subtitles',
            },
            false,
          );
        }
      });

      videoObj = null;
      // videoOptions = null;
    },
    dispose(callback) {
      if (this.player && this.player.dispose) {
        if (this.player.techName_ !== 'Flash' && this.player.pause) {
          this.player.pause();
        }

        const remoteTextTracks = this.player.remoteTextTracks();
        // Do all those deletes due to strange memory usage (memory leaks)
        for (let i = remoteTextTracks.length - 1; i >= 0; i -= 1) {
          if (!isNil(remoteTextTracks[i].activeCues_)) {
            remoteTextTracks[i].activeCues_.forEach((val, key) => {
              delete remoteTextTracks[i].activeCues_[key];
            });
            remoteTextTracks[i].activeCues_.length = 0;
          }

          if (!isNil(remoteTextTracks[i].cues_)) {
            remoteTextTracks[i].cues_.forEach((val, key) => {
              delete remoteTextTracks[i].cues_[key];
            });
            remoteTextTracks[i].cues_.length = 0;
          }

          if (!isNil(remoteTextTracks[i].lastCue)) {
            delete remoteTextTracks[i].lastCue;
          }

          this.player.removeRemoteTextTrack(remoteTextTracks[i]);
        }

        this.player.dispose();
        this.player = null;

        this.$nextTick(() => {
          this.reseted = false;
          this.$nextTick(() => {
            this.reseted = true;
            this.$nextTick(() => {
              if (callback) callback();
            });
          });
        });
      }
    },
    handleVisibilityChange() {
      const self = this;

      // Update video for iOS devices after unlocked
      if (!document.hidden && !isNil(self.player) && isMobile().apple.device) {
        self.player.play();
      }
    },
  },

  watch: {
    options: {
      deep: true,
      handler(options) {
        this.dispose(() => {
          if (options && options.sources && options.sources.length) {
            this.initialize();
          }
        });
      },
    },
  },

  beforeUnmount() {
    if (this.player) {
      this.dispose();
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange, false);
  },

  mounted() {
    if (!this.player) {
      this.initialize();
    }

    document.addEventListener('visibilitychange', this.handleVisibilityChange, false);
  },

  beforeDestroy() {
    if (this.player) {
      this.dispose();
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange, false);
  },
};
</script>

<style></style>
